import {useEffect, useState} from 'react';
import {useParams, Link, useHistory} from 'react-router-dom';
import {Button, Select, Spin, Typography, Collapse, Input} from 'antd';

import {Plan, Test} from '../../graphql/API';
import {PlansService} from './PlansService';
import {TestsService} from '../Test';
import {LevelsService} from '../Level/LevelsService';
import {CurriculumsService} from '../Curriculums/CurriculumsService';
import {DailyExerciseService} from '@PikaDoApp/npm-pikado-logic';

const plansService = new PlansService();
const testsService = new TestsService();
const levelsService = new LevelsService();
const curriculumsService = new CurriculumsService();
const dailyExerciseService = new DailyExerciseService();
export const PlanEdit = () => {
  const history = useHistory();

  const [testsSearchResult, setTestsSearchResult] = useState<Array<Test>>();
  const [allFetchedTests, setAllFetchedTests] = useState<Array<Test>>([]);
  // OLD CODE - Commented out (kept for reference)
  // const [plan, setPlan] = useState<Plan | undefined>(undefined);

  // NEW CODE - Using @PikaDoApp/npm-pikado-logic
  const [plan, setPlan] = useState<any | undefined>(undefined);
  // COMMENTED OUT - No longer needed since UI is hidden
  // const [planName, setPlanName] = useState<string>('');
  // const [planDuration, setPlanDuration] = useState<number>(0);
  // const [selectedLevelId, setSelectedLevelId] = useState<string>('');
  // const [selectedCurriculumId, setSelectedCurriculumId] = useState<string>('');
  // const [levels, setLevels] = useState<Array<any>>([]);
  // const [curriculums, setCurriculums] = useState<Array<any>>([]);
  const [exerciseMap, setExerciseMap] = useState<{[id: string]: any}>({});
  const [allExercises, setAllExercises] = useState<Array<any>>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchResults, setSearchResults] = useState<Array<any>>([]);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>('');
  const [isError, setIsError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // State for lazy-loaded daily exercises
  const [originalDayExercises, setOriginalDayExercises] = useState<Map<number, string[]>>(new Map());
  const [currentDayExercises, setCurrentDayExercises] = useState<Map<number, string[]>>(new Map());
  const [loadedDayDetails, setLoadedDayDetails] = useState<Map<number, any[]>>(new Map());
  const [loadingDays, setLoadingDays] = useState<Set<number>>(new Set());

  let params: {planId: string} = useParams();

  const {Panel} = Collapse;
  const {Title} = Typography;

  // OLD CODE - Commented out (kept for reference)
  // useEffect(() => {
  //   plansService
  //     .getPlanById(params.planId)
  //     .then((resp) => {
  //       setIsLoading(false);
  //       console.log('resp->', resp);
  //       setPlan(resp);
  //     })
  //     .catch((error) => {
  //       alert('Fetching plans error');
  //       //console.log(error);
  //       setIsLoading(false);
  //       setIsError(true);
  //     });
  // }, []);

  // COMMENTED OUT - No longer needed since UI is hidden
  // // Fetch levels and curriculums for dropdowns
  // useEffect(() => {
  //   console.log('🔍 [PLAN-EDIT] Fetching levels and curriculums');

  //   levelsService
  //     .getNewLevels()
  //     .then((lvls) => {
  //       console.log('✅ [PLAN-EDIT] Levels fetched:', lvls);
  //       setLevels(lvls);
  //     })
  //     .catch((error) => {
  //       console.error('❌ [PLAN-EDIT] Error fetching levels:', error);
  //     });

  //   curriculumsService
  //     .getCurriculums()
  //     .then((currs) => {
  //       console.log('✅ [PLAN-EDIT] Curriculums fetched:', currs);
  //       setCurriculums(currs);
  //     })
  //     .catch((error) => {
  //       console.error('❌ [PLAN-EDIT] Error fetching curriculums:', error);
  //     });
  // }, []);

  // Fetch all daily exercises for search
  useEffect(() => {
    dailyExerciseService
      .getAllDailyExercises(1, 1000) // Fetch first 1000 exercises
      .then((resp) => {
        const exercises = resp?.data || [];
        setAllExercises(exercises);

        // Create exercise map for quick lookup
        const map: {[id: string]: any} = {};
        exercises.forEach((ex: any) => {
          const exId = ex.id || ex._id;
          if (exId) {
            map[exId] = ex;
          }
        });
        setExerciseMap(map);
      })
      .catch((error) => {
        console.error('❌ [PLAN-EDIT] Error fetching exercises:', error);
      });
  }, []);

  // Debounce search query by 300ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Execute search when debounced value changes
  useEffect(() => {
    const handleSearch = async (term: string) => {
      setIsSearching(true);
      try {
        const response = await dailyExerciseService.searchDailyExercisesByName(
          term,
          1,
          5000 // Large limit to find all matching exercises
        );
        const exercises = response?.data || [];
        setSearchResults(exercises);
      } catch (error) {
        console.error('❌ [PLAN-EDIT] Error searching exercises:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    };

    if (debouncedSearchQuery.length >= 3) {
      handleSearch(debouncedSearchQuery);
    } else if (debouncedSearchQuery.length === 0) {
      setSearchResults([]);
    }
  }, [debouncedSearchQuery]);

  // Fetch plan data
  useEffect(() => {
    plansService
      .getNewPlanById(params.planId)
      .then((resp) => {
        setIsLoading(false);
        setPlan(resp);
        // COMMENTED OUT - No longer needed since UI is hidden
        // setPlanName(resp?.name || '');
        // setPlanDuration(resp?.duration || 0);

        // // Set selected level and curriculum IDs
        // const levelId = typeof resp.level === 'object' ? (resp.level?.id || resp.level?._id) : resp.level;
        // const curriculumId = typeof resp.curriculum === 'object' ? (resp.curriculum?.id || resp.curriculum?._id) : resp.curriculum;
        // setSelectedLevelId(levelId || '');
        // setSelectedCurriculumId(curriculumId || '');

        // console.log('✅ [PLAN-EDIT] Selected IDs:', {levelId, curriculumId});
        console.log('✅ [PLAN-EDIT] Plan loaded - daily exercises will be loaded on-demand per day');
      })
      .catch((error) => {
        console.error('❌ [PLAN-EDIT] Error fetching plan:', error);
        alert('Fetching plans error');
        setIsLoading(false);
        setIsError(true);
      });
  }, [params.planId]);

  // Load exercises for a specific day
  const loadDayExercises = async (dayNumber: number) => {
    // Check if already loaded or loading
    if (loadedDayDetails.has(dayNumber) || loadingDays.has(dayNumber)) {
      return;
    }

    setLoadingDays(new Set(loadingDays).add(dayNumber));

    try {
      const exercises = await plansService.getDailyExercisesByPlanAndDay(
        params.planId,
        dayNumber,
      );

      // Create "name+id" strings for each exercise
      const exerciseStrings = exercises.map((ex: any) => {
        const exId = ex.id || ex._id;
        const exName = ex.name || exId;
        return exName + '+' + exId;
      });

      // Store in originalDayExercises if first load
      if (!originalDayExercises.has(dayNumber)) {
        setOriginalDayExercises(new Map(originalDayExercises).set(dayNumber, exerciseStrings));
      }

      // Store in currentDayExercises if first load
      if (!currentDayExercises.has(dayNumber)) {
        setCurrentDayExercises(new Map(currentDayExercises).set(dayNumber, exerciseStrings));
      }

      // Store full exercise details
      setLoadedDayDetails(new Map(loadedDayDetails).set(dayNumber, exercises));

      // Remove from loading set
      const newLoadingDays = new Set(loadingDays);
      newLoadingDays.delete(dayNumber);
      setLoadingDays(newLoadingDays);
    } catch (error) {
      console.error('❌ [PLAN-EDIT] Error loading exercises for day', dayNumber, ':', error);

      // Remove from loading set
      const newLoadingDays = new Set(loadingDays);
      newLoadingDays.delete(dayNumber);
      setLoadingDays(newLoadingDays);
    }
  };

  // OLD CODE - Commented out (kept for reference)
  // const renderDay = (plan: Plan) => {
  //   return Array.from({length: 365}, (_, dayObject) => dayObject + 1).map(
  //     (dayObject) => {
  //       return (
  //         <Panel header={`d${dayObject}`} key={dayObject}>
  //           <Button onClick={async (e) => { ... }}>
  //             Get plan of day {dayObject}
  //           </Button>
  //           <Select ... >
  //             ...
  //           </Select>
  //         </Panel>
  //       );
  //     },
  //   );
  // };

  const renderDay = (plan: any) => {
    const duration = plan?.duration || 365;

    // Use search results if searching, otherwise use all fetched exercises
    const filteredExercises = searchQuery.length >= 3
      ? searchResults
      : allExercises;

    return Array.from({length: duration}, (_, dayIndex) => {
      const dayNumber = dayIndex + 1;
      const currentExercises = currentDayExercises.get(dayIndex) || [];
      const isLoadingDay = loadingDays.has(dayIndex);

      return (
        <Panel
          header={`Day ${dayNumber}`}
          key={dayIndex}
          extra={
            isLoadingDay ? 'Loading...' : `${currentExercises.length} exercise(s)`
          }>
          {isLoadingDay ? (
            <Spin tip="Loading exercises..." />
          ) : (
            <Select
              autoClearSearchValue={false}
              mode="multiple"
              loading={isSearching}
              style={{width: '100%'}}
              placeholder="Please select exercises for this day"
              filterOption={false}
              value={currentExercises}
              onSearch={(searchValue) => {
                setSearchQuery(searchValue);
              }}
              onChange={(idWithNameList) => {

                // Keep the full "name+id" strings
                const exerciseStrings = idWithNameList || [];

                // Update currentDayExercises map
                const newCurrentDayExercises = new Map(currentDayExercises);
                newCurrentDayExercises.set(dayIndex, exerciseStrings);
                setCurrentDayExercises(newCurrentDayExercises);

              }}>
              {filteredExercises.map((ex: any) => {
                const exId = ex.id || ex._id;
                return (
                  <Select.Option key={exId} value={ex.name + '+' + exId}>
                    <p>{ex.name}</p>
                  </Select.Option>
                );
              })}
            </Select>
          )}
        </Panel>
      );
    });
  };

  return (
    <div style={{padding: 10}}>
      <Title style={{textAlign: 'center'}}>Plan Edit</Title>

      {/* COMMENTED OUT - Plan metadata editing UI (name, duration, level, curriculum) */}
      {/* {!isError && !isLoading && plan && (
        <div style={{marginBottom: 20, padding: 10, border: '1px solid #d9d9d9', borderRadius: 4}}>
          <div style={{marginBottom: 10}}>
            <label style={{fontWeight: 'bold', marginRight: 10}}>Plan Name:</label>
            <Input
              value={planName}
              onChange={(e) => setPlanName(e.target.value)}
              placeholder="Enter plan name"
              style={{width: 300}}
            />
          </div>
          <div style={{marginBottom: 10}}>
            <label style={{fontWeight: 'bold', marginRight: 10}}>Duration (days):</label>
            <Input
              type="number"
              value={planDuration}
              onChange={(e) => setPlanDuration(parseInt(e.target.value) || 0)}
              placeholder="Enter duration"
              style={{width: 150}}
            />
          </div>
          <div style={{marginBottom: 10}}>
            <label style={{fontWeight: 'bold', marginRight: 10}}>Level:</label>
            <Select
              value={selectedLevelId}
              showSearch
              style={{width: 300}}
              placeholder="Select a Level"
              optionFilterProp="children"
              onChange={(levelId) => setSelectedLevelId(levelId)}>
              {levels?.map((level) => (
                <Select.Option key={level.id || level._id} value={level.id || level._id}>
                  {level.name}
                </Select.Option>
              ))}
            </Select>
          </div>
          <div style={{marginBottom: 10}}>
            <label style={{fontWeight: 'bold', marginRight: 10}}>Curriculum:</label>
            <Select
              value={selectedCurriculumId}
              showSearch
              style={{width: 300}}
              placeholder="Select a Curriculum"
              optionFilterProp="children"
              onChange={(curriculumId) => setSelectedCurriculumId(curriculumId)}>
              {curriculums?.map((curriculum) => (
                <Select.Option key={curriculum.id || curriculum._id} value={curriculum.id || curriculum._id}>
                  {curriculum.name}
                </Select.Option>
              ))}
            </Select>
          </div>
        </div>
      )} */}

      {!isError && !isLoading && (
        <>
          <Collapse
            onChange={(activeKeys) => {
              // Load exercises for expanded panels
              if (Array.isArray(activeKeys)) {
                activeKeys.forEach((key) => {
                  const dayIndex = Number(key);
                  if (!isNaN(dayIndex)) {
                    loadDayExercises(dayIndex);
                  }
                });
              }
            }}>
            {plan && renderDay(plan)}
          </Collapse>
        </>
      )}
      <div style={{width: '100%', textAlign: 'center', marginTop: '20px'}}>
        {/* OLD CODE - Commented out (kept for reference) */}
        {/* <Button onClick={async () => {
            let planObj = plan;
            let planId = plan?.id;
            let updatedAt = plan?.updatedAt;
            ... complex transformation logic ...
            await plansService.updatePlan(planId!, updatedAt!, JSON.stringify(extraPlan))
          }}>
          Save
        </Button> */}

        {/* NEW CODE - Using @PikaDoApp/npm-pikado-logic */}
        <Button
          style={{}}
          size={'large'}
          loading={isSaving}
          disabled={isSaving}
          onClick={async () => {
            setIsSaving(true);

            const planId = plan?.id;

            try {
              // Step 1: Update the plan metadata (using original values to preserve name, duration, level, curriculum)
              console.log('🔍 [PLAN-EDIT] Step 1: Updating plan metadata...');

              // Extract original level and curriculum IDs
              const originalLevelId = typeof plan.level === 'object' ? (plan.level?.id || plan.level?._id) : plan.level;
              const originalCurriculumId = typeof plan.curriculum === 'object' ? (plan.curriculum?.id || plan.curriculum?._id) : plan.curriculum;

              await plansService.newUpdatePlan(
                planId,
                plan.name,              // Use original plan name
                plan.duration,          // Use original plan duration
                originalLevelId,        // Use original level ID
                originalCurriculumId,   // Use original curriculum ID
                plan.placementTest
              );

              // Get all day indices that have been loaded (and potentially modified)
              const allDayIndices = new Set([
                ...Array.from(originalDayExercises.keys()),
                ...Array.from(currentDayExercises.keys()),
              ]);

              for (const dayIndex of allDayIndices) {
                const originalExercises = originalDayExercises.get(dayIndex) || [];
                const currentExercises = currentDayExercises.get(dayIndex) || [];

                // Extract IDs from "name+id" strings
                const originalIds = originalExercises.map((str: string) => str.split('+')[1]);
                const currentIds = currentExercises.map((str: string) => str.split('+')[1]);

                // Find exercises to add (in current but not in original)
                const exercisesToAdd = currentIds.filter(
                  (exId: string) => !originalIds.includes(exId)
                );

                // Find exercises to remove (in original but not in current)
                const exercisesToRemove = originalIds.filter(
                  (exId: string) => !currentIds.includes(exId)
                );

               

                // Add new exercises
                for (const exerciseId of exercisesToAdd) {
                  await plansService.addExerciseToDay(planId, dayIndex, exerciseId);
                }

                // Remove old exercises
                for (const exerciseId of exercisesToRemove) {
                  await plansService.removeExerciseFromDay(planId, dayIndex, exerciseId);
                }
              }

              alert('Plan edited successfully! All changes have been saved.');
              setIsSaving(false);
              history.push('/plans/' + planId);
            } catch (error: any) {
              console.error('❌ [PLAN-EDIT] Error updating plan:', error);
              alert('Plan edit failed: ' + (error?.message || JSON.stringify(error)));
              setIsSaving(false);
            }
          }}>
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </div>
      {isLoading && (
        <Spin size="large" style={{margin: 'auto', width: '100%'}} />
      )}
      {isError && <Title>Plan fetching error ⚠️</Title>}
    </div>
  );
};
